/** Вкладка «Дерево»: блок «Иерархия» (разрезы, пресеты, глазик), ленивое раскрытие (POST /tree с path), сравнение.
 *
 *  Каждый раскрытый узел — свой запрос следующего уровня (useInfiniteQuery: «показать ещё» —
 *  следующая страница offset). Раскрытия живут в состоянии вкладки и сбрасываются при смене
 *  уровней/фильтров (ключ компонента), в адрес не пишутся. */
import { useInfiniteQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { analyticsApi } from "@/api/mpAnalytics";
import type {
  OzFilters,
  PathStep,
  TreeRow,
  TreeSort,
} from "@/api/ozonAnalytics";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { childBase, nodeMarks, type ShareBase } from "./calc";
import { cogsNullTitle } from "./cogs";
import {
  TREE_LABEL_CH,
  marginHidden,
  treeCellNumbers,
  treeColumns,
  treeCompactCells,
  type TreeCogs,
  type TreeColumn,
  type TreeMetricId,
} from "./compact";
import { useMp } from "./mp";
import { useSummary } from "./queries";
import { HierarchyPanel } from "./HierarchyPanel";
import { ProductHover, ProductLink } from "./ProductCard";
import { CardButton, CompactTd, ErrorBox } from "./shared";
import {
  activeLevels,
  unknownLevels,
  type CardRef,
  type Hierarchy,
  type PageState,
} from "./state";
import { SupplyIssuesDetails } from "./SupplyIssues";
import {
  VARIANT_LABEL,
  colSortPatch,
  legacyColSortKey,
  columnSortKeys,
  effectiveColSort,
  nextColSort,
  sortTreeRows,
  variantOf,
  type ColSort,
} from "./treeSort";
import { apiErrorText, selectClass, thClass } from "./ui";

const TREE_PAGE = 100;
/** Поиск короче — слишком широкий и дорогой запрос (ILIKE по пяти колонкам): не отправляем. */
export const MIN_SEARCH = 2;

const SORTS: { id: TreeSort; label: string }[] = [
  { id: "abs", label: "по величине" },
  { id: "rev", label: "по выручке" },
  { id: "cost", label: "по расходам" },
  { id: "delta", label: "по Δ к сравнению" },
];

/** Настройки дерева — не меняются при раскрытии узлов (строки под memo не перерисовываются). */
interface TreeCtxValue {
  filters: OzFilters;
  levels: string[];
  search: string;
  sort: TreeSort;
  hasCmp: boolean;
  /** площадка («Ozon», «WB») — в подсказках «—» и состава расходов */
  mpLabel: string;
  dimLabel: (code: string) => string;
  /** пометка корня о сортировке без себестоимости (`sort_note` ответа) — в шапку вкладки */
  onSortNote: (note: string | null) => void;
  /** открыть карточку сущности (боковая панель) */
  openCard: (card: CardRef) => void;
  toggle: (key: string) => void;
}

const TreeCtx = createContext<TreeCtxValue | null>(null);
/** Раскрытые узлы — отдельный контекст: его читает только TreeNode. */
const OpenCtx = createContext<ReadonlySet<string>>(new Set());
/** Действующая сортировка по столбцу — отдельный контекст: читает только TreeLevel (смена
 *  порядка не перестраивает дерево и не сбрасывает раскрытия). */
const ColSortCtx = createContext<ColSort | null>(null);

function useTreeCtx(): TreeCtxValue {
  const v = useContext(TreeCtx);
  if (!v) throw new Error("TreeCtx не задан");
  return v;
}

const pathKey = (path: PathStep[]): string =>
  path.map((p) => `${p.dim}=${p.key}`).join("\u0001");

export function TreeTab({
  state,
  filters,
  dims,
  onLevels,
  onPreset,
  onPatch,
  onCard,
}: {
  state: PageState;
  filters: OzFilters;
  /** измерения из /meta (код → название); null — мета ещё не пришла */
  dims: { code: string; label: string }[] | null;
  onLevels: (h: Hierarchy) => void;
  onPreset: (id: string) => void;
  onPatch: (patch: Record<string, string | null>) => void;
  onCard: (card: CardRef) => void;
}): React.ReactElement {
  const mp = useMp();
  const hasCmp = filters.cmp_from !== null;
  // ключ Δ без сравнения не действует: дерево — в порядке бэкенда, над таблицей — пометка
  const colSort = effectiveColSort(state.colSort, hasCmp);
  const onColSort = useCallback(
    (s: ColSort | null) => onPatch(colSortPatch(s)),
    [onPatch],
  );
  // прежний ключ «Чистой маржи» в адресе (`tsort=net…`) — переписать на ключ «Маржи» один раз;
  // прочие неразобранные (`cogs…`, опечатки) — ошибка с «Сбросить», а не молча без сортировки
  const legacyKey = legacyColSortKey(state.colSortBad);
  useEffect(() => {
    if (legacyKey) onPatch({ tsort: legacyKey });
  }, [legacyKey, onPatch]);
  const badColSort = state.colSortBad !== null && legacyKey === null ? state.colSortBad : null;
  // сортировка корня шла без себестоимости — пометка у «Сортировки»
  const [sortNote, setSortNote] = useState<string | null>(null);
  const summary = useSummary(mp.id, filters);
  const rootBase: ShareBase | null = summary.data
    ? { rev: summary.data.totals.cur.revenue, name: "вся выручка периода" }
    : null;
  const rootBaseCmp: ShareBase | null = summary.data?.totals.cmp
    ? {
        rev: summary.data.totals.cmp.revenue,
        name: "вся выручка периода сравнения",
      }
    : null;

  // поиск: печатаем в поле, в адрес (и в запрос) — с задержкой
  const [searchDraft, setSearchDraft] = useState(state.search);
  // адрес → черновик, но не перетирать набираемое: «abc » и «abc» — один и тот же поиск
  useEffect(
    () => setSearchDraft((d) => (d.trim() === state.search ? d : state.search)),
    [state.search],
  );
  const draftTrim = searchDraft.trim();
  const tooShort = draftTrim.length > 0 && draftTrim.length < MIN_SEARCH;
  useEffect(() => {
    if (draftTrim === state.search || tooShort) return;
    const t = setTimeout(() => onPatch({ q: draftTrim || null }), 350);
    return () => clearTimeout(t);
  }, [draftTrim, tooShort, state.search, onPatch]);
  // ссылка с коротким q= тоже не уходит на бэкенд
  const search = state.search.length >= MIN_SEARCH ? state.search : "";

  const hierarchy: Hierarchy = { levels: state.levels, off: state.levelsOff };
  // в запрос и ключ раскрытий — только активные разрезы (выключенные глазиком не идут)
  const levels = activeLevels(hierarchy);
  const dimLabel = useCallback(
    (code: string) => dims?.find((d) => d.code === code)?.label ?? code,
    [dims],
  );

  // ключ раскрытий: при смене фильтров/уровней/поиска/сортировки дерево строится заново
  const resetKey = JSON.stringify([filters, levels, search, state.sort]);
  // разрезы из адреса, которых у площадки нет (ссылка от другой площадки): дерево не строим
  const unknown = dims ? unknownLevels(state.levels, dims) : [];
  const defaultPreset = mp.treePresets[0]!;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          placeholder={mp.searchPlaceholder}
          aria-label="Поиск по дереву"
          className="h-8 w-80 text-xs"
        />
        {tooShort && (
          <span
            className="text-xs text-amber-700 dark:text-amber-400"
            data-testid="search-hint"
          >
            Введите не меньше {MIN_SEARCH} символов — короче поиск не
            отправляется
          </span>
        )}
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Сортировка
          <select
            className={selectClass}
            value={state.sort}
            onChange={(e) =>
              onPatch({
                sort: e.target.value === "abs" ? null : e.target.value,
              })
            }
          >
            {SORTS.map((s) => (
              <option
                key={s.id}
                value={s.id}
                disabled={s.id === "delta" && !hasCmp}
              >
                {s.label}
              </option>
            ))}
          </select>
        </label>
        {sortNote && unknown.length === 0 && (
          <span className="text-xs text-amber-700 dark:text-amber-400" data-testid="tree-sort-note">
            {sortNote}
          </span>
        )}
      </div>

      <HierarchyPanel
        hierarchy={hierarchy}
        dims={dims}
        presets={mp.treePresets}
        treePreset={state.treePreset}
        onLevels={onLevels}
        onPreset={onPreset}
      />

      {badColSort !== null && (
        <div
          role="alert"
          data-testid="tree-sort-bad"
          className="flex flex-wrap items-center gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
        >
          <span>
            Сортировки по столбцу «{badColSort}» нет (ссылка устарела: колонок «Закупочная цена» и
            «Чистая маржа» больше нет) — дерево в порядке «{SORTS.find((s) => s.id === state.sort)?.label}».
          </span>
          <button
            type="button"
            className="font-medium underline"
            onClick={() => onPatch({ tsort: null, tdir: null })}
          >
            Сбросить
          </button>
        </div>
      )}

      {state.colSort && !colSort && (
        <div
          data-testid="tree-sort-unavailable"
          className="flex flex-wrap items-center gap-3 text-xs text-amber-700 dark:text-amber-400"
        >
          <span>
            Сортировка по столбцу Δ ({VARIANT_LABEL[variantOf(state.colSort.key)]}) работает
            только со сравнением периодов — сейчас дерево в порядке «{SORTS.find((s) => s.id === state.sort)?.label}».
          </span>
          <button
            type="button"
            className="font-medium underline"
            onClick={() => onColSort(null)}
          >
            Сбросить
          </button>
        </div>
      )}

      {unknown.length > 0 ? (
        <div
          role="alert"
          data-testid="tree-unknown-levels"
          className="flex flex-wrap items-center gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
        >
          <span>
            Разрезов {unknown.map((c) => `«${c}»`).join(", ")} нет у площадки {mp.label} —
            дерево не построено. Поменяйте их в «Иерархии» или сбросьте.
          </span>
          <button
            type="button"
            className="text-xs font-medium underline"
            onClick={() => onPreset(defaultPreset.id)}
          >
            Сбросить на «{defaultPreset.label}»
          </button>
        </div>
      ) : (
        <TreeTable
          key={resetKey}
          ctxBase={{
            filters,
            levels,
            search,
            sort: state.sort,
            hasCmp,
            mpLabel: mp.label,
            dimLabel,
            onSortNote: setSortNote,
            openCard: onCard,
          }}
          rootBase={rootBase}
          rootBaseCmp={rootBaseCmp}
          colSort={colSort}
          onColSort={onColSort}
        />
      )}
      {summary.isError && (
        <p className="text-xs text-red-600 dark:text-red-400">
          Без сводки нет выручки периода — доля расходов у узлов верхнего
          уровня без выручки не посчитана. {apiErrorText(summary.error, mp.label)}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Метки: <MarkChip kind="alloc" /> — вся сумма узла начислена {mp.label} без
        артикула и разложена по правилу (какому — видно на уровне «Способ
        привязки»); <MarkChip kind="common" /> — расходы, не привязанные к
        артикулу. Выручка − Расходы = Маржа; расходы — все удержания {mp.label} и
        себестоимость, без себестоимости (период раньше её начала) расходов и маржи нет — «—».
        У узлов без выручки маржи % нет, а доля расходов — от выручки ближайшего узла выше, у
        которого она есть.
      </p>
    </div>
  );
}

function MarkChip({
  kind,
  title,
}: {
  kind: "alloc" | "common";
  title?: string;
}): React.ReactElement {
  return (
    <span
      title={title}
      className={cn(
        "ml-1.5 inline-block rounded-full px-1.5 py-px text-[10.5px] font-normal",
        kind === "alloc"
          ? "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300"
          : "bg-muted text-muted-foreground",
      )}
    >
      {kind === "alloc" ? "распределено" : "общие"}
    </span>
  );
}

function TreeTable({
  ctxBase,
  rootBase,
  rootBaseCmp,
  colSort,
  onColSort,
}: {
  ctxBase: Omit<TreeCtxValue, "toggle">;
  rootBase: ShareBase | null;
  rootBaseCmp: ShareBase | null;
  colSort: ColSort | null;
  onColSort: (s: ColSort | null) => void;
}): React.ReactElement {
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = useCallback(
    (key: string) =>
      setOpen((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [],
  );
  // фильтры, уровни, поиск и сортировка входят в key компонента (смена = новое дерево);
  // меняться на лету могут только подписи измерений и колбэк карточки
  const ctx = useMemo<TreeCtxValue>(
    () => ({ ...ctxBase, toggle }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toggle, ctxBase.dimLabel, ctxBase.openCard],
  );
  return (
    <TreeCtx.Provider value={ctx}>
      <OpenCtx.Provider value={open}>
        <ColSortCtx.Provider value={colSort}>
          <TreeTableView
            hasCmp={ctxBase.hasCmp}
            sort={colSort}
            onSort={onColSort}
          >
            <TreeLevel
              path={EMPTY_PATH}
              depth={0}
              base={rootBase}
              baseCmp={rootBaseCmp}
            />
          </TreeTableView>
        </ColSortCtx.Provider>
      </OpenCtx.Provider>
    </TreeCtx.Provider>
  );
}

const EMPTY_PATH: PathStep[] = [];

/** Число колонок таблицы дерева (для строк «загружаю» / «показать ещё»); подписи не важны. */
function treeColSpan(hasCmp: boolean): number {
  return 1 + treeColumns(hasCmp).length;
}

/** Родительный падеж метрики — в заголовке меню сортировки колонок Δ. */
const METRIC_GEN: Record<TreeMetricId, string> = {
  rev: "выручки",
  cost: "расходов",
  margin: "маржи",
};

/** Заголовок колонки — кнопка: меню вариантов сортировки этой колонки (₽ / шт / % / п.п.).
 *  Новый вариант — по убыванию, тот же ещё раз — по возрастанию; «Сбросить» — порядок бэкенда. */
function SortHead({
  col,
  sort,
  onSort,
}: {
  col: TreeColumn;
  sort: ColSort | null;
  onSort: (s: ColSort | null) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const keys = columnSortKeys(col);
  const active = sort && keys.includes(sort.key) ? sort : null;
  const arrow = (s: ColSort): string => (s.dir === "desc" ? "↓" : "↑");
  const name =
    col.role === "value"
      ? col.head
      : `${col.role === "pp" ? "Δ п.п." : "Δ"} ${METRIC_GEN[col.metric]}`;
  const pick = (s: ColSort | null): void => {
    onSort(s);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={col.title ?? undefined}
          className={cn(
            "inline-flex items-center gap-1 rounded-sm uppercase tracking-wide hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            active && "text-foreground",
          )}
        >
          {col.head}
          {active && (
            <span data-testid="tree-sort-mark" className="normal-case">
              {arrow(active)} {VARIANT_LABEL[variantOf(active.key)]}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-48 p-1 text-xs">
        <div className="px-2 py-1 text-[11px] text-muted-foreground">
          Сортировка: {name}
        </div>
        <div role="group" aria-label={`Сортировка: ${name}`}>
          {keys.map((k) => {
            const on = active?.key === k ? active : null;
            return (
              <button
                key={k}
                type="button"
                aria-pressed={on !== null}
                onClick={() => pick(nextColSort(sort, k))}
                className={cn(
                  "flex w-full items-center justify-between rounded px-2 py-1 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none",
                  on && "font-medium",
                )}
              >
                <span>по {VARIANT_LABEL[variantOf(k)]}</span>
                <span className="text-muted-foreground">
                  {on ? (on.dir === "desc" ? "↓ по убыванию" : "↑ по возрастанию") : ""}
                </span>
              </button>
            );
          })}
        </div>
        {sort && (
          <button
            type="button"
            onClick={() => pick(null)}
            className="mt-1 w-full rounded border-t px-2 py-1 text-left text-muted-foreground hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
          >
            Сбросить
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Таблица дерева: Выручка − Расходы = Маржа; при сравнении у каждой — Δ (₽, под ними %), у
 *  расходов и маржи левее Δ — ещё Δ п.п. Заголовки колонок — меню сортировки по столбцу.
 *  Шапка липкая: у контейнера своя вертикальная прокрутка (`max-h` + `overflow-auto`, как в
 *  «По дням»), фон шапки непрозрачный — под ней прокручиваются строки. */
function TreeTableView({
  hasCmp,
  sort,
  onSort,
  children,
}: {
  hasCmp: boolean;
  sort: ColSort | null;
  onSort: (s: ColSort | null) => void;
  children: React.ReactNode;
}): React.ReactElement {
  const cols = treeColumns(hasCmp);
  return (
    <div
      className="max-h-[75vh] w-fit max-w-full overflow-auto rounded-md border bg-card"
      data-testid="tree-scroll"
    >
      {/* ширины колонок заданы заранее (см. compact.ts) — не тянутся по экрану и не прыгают при раскрытии */}
      <table
        className="table-fixed text-xs"
        style={{
          width: `${TREE_LABEL_CH + cols.reduce((a, c) => a + c.ch, 0)}ch`,
        }}
        data-testid="tree-table"
      >
        <colgroup>
          <col style={{ width: `${TREE_LABEL_CH}ch` }} />
          {cols.map((c, i) => (
            <col key={i} style={{ width: `${c.ch}ch` }} />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-10 bg-muted text-[11px]" data-testid="tree-head">
          <tr>
            <th className={cn(thClass, "text-left")}>Узел</th>
            {cols.map((c, i) => (
              <th
                key={i}
                className={cn(thClass, hasCmp && c.role === "value" && "border-l")}
                aria-sort={
                  sort && columnSortKeys(c).includes(sort.key)
                    ? sort.dir === "desc"
                      ? "descending"
                      : "ascending"
                    : undefined
                }
              >
                <SortHead col={c} sort={sort} onSort={onSort} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function TreeLevel({
  path,
  depth,
  base,
  baseCmp,
}: {
  path: PathStep[];
  depth: number;
  base: ShareBase | null;
  baseCmp: ShareBase | null;
}): React.ReactElement {
  const { filters, levels, search, sort, hasCmp, mpLabel, onSortNote } = useTreeCtx();
  const colSort = useContext(ColSortCtx);
  const mp = useMp().id;
  const dim = levels[depth]!;
  const q = useInfiniteQuery({
    queryKey: ["mp-analytics", mp, "tree", filters, levels, search, sort, path],
    queryFn: ({ pageParam, signal }) =>
      analyticsApi(mp).postTree(
        {
          ...filters,
          levels,
          path,
          search: search || null,
          sort,
          limit: TREE_PAGE,
          offset: pageParam,
        },
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((s, p) => s + p.rows.length, 0);
      return loaded < last.total_count ? loaded : undefined;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  // корень сообщает, шла ли его сортировка без себестоимости (`sort_note`)
  const rootNote = depth === 0 ? (q.data?.pages[0]?.sort_note ?? null) : undefined;
  useEffect(() => {
    if (rootNote !== undefined) onSortNote(rootNote);
  }, [rootNote, onSortNote]);
  // «—» расходов и маржи — с подсказкой по блоку `cogs` этого же ответа (с какой даты / нет цен)
  const cogsView = q.data?.pages[0]?.cogs;
  const cogs = useMemo<TreeCogs>(
    () => ({
      nullTitle: cogsNullTitle(cogsView, mpLabel, "cur"),
      nullTitleCmp: cogsNullTitle(cogsView, mpLabel, "cmp"),
      mpLabel,
    }),
    [cogsView, mpLabel],
  );
  // сортировка по столбцу — среди загруженных соседей, по числам, показанным в ячейках
  const rows = useMemo(() => {
    const loaded = (q.data?.pages ?? []).flatMap((p) => p.rows);
    if (!colSort) return loaded;
    const hideMargin = marginHidden([...path.map((p) => p.dim), dim]);
    return sortTreeRows(loaded, colSort, (r) =>
      treeCellNumbers(r, base, baseCmp, hasCmp, hideMargin, cogs),
    );
  }, [q.data, colSort, path, dim, base, baseCmp, hasCmp, cogs]);
  const colSpan = treeColSpan(hasCmp);
  const indent = { paddingLeft: `${12 + depth * 18}px` };

  if (q.isLoading) {
    return (
      <tr>
        <td
          colSpan={colSpan}
          className="py-2 text-xs text-muted-foreground"
          style={indent}
        >
          Загружаю…
        </td>
      </tr>
    );
  }
  if (q.isError && !q.data) {
    return (
      <tr>
        <td colSpan={colSpan} className="py-2 pr-2" style={indent}>
          <ErrorBox error={q.error} what="уровень дерева" />
        </td>
      </tr>
    );
  }
  const total = q.data?.pages[0]?.total_count ?? 0;
  if (rows.length === 0) {
    return (
      <tr>
        <td
          colSpan={colSpan}
          className="py-2 text-xs text-muted-foreground"
          style={indent}
        >
          {search ? "Ничего не найдено." : "Нет начислений."}
        </td>
      </tr>
    );
  }
  return (
    <>
      {rows.map((r) => (
        <TreeNode
          key={r.key}
          row={r}
          dim={dim}
          depth={depth}
          path={path}
          base={base}
          baseCmp={baseCmp}
          cogs={cogs}
        />
      ))}
      {q.hasNextPage && (
        <tr>
          <td
            colSpan={colSpan}
            className="py-1"
            style={{ paddingLeft: `${30 + depth * 18}px` }}
          >
            <button
              type="button"
              className="text-xs text-primary hover:underline disabled:opacity-50"
              disabled={q.isFetchingNextPage}
              onClick={() => void q.fetchNextPage()}
            >
              {q.isFetchingNextPage
                ? "Загружаю…"
                : `Показать ещё ${Math.min(TREE_PAGE, total - rows.length)} из ${total - rows.length}`}
            </button>
            {colSort && (
              <span
                className="ml-3 text-xs text-amber-700 dark:text-amber-400"
                data-testid="tree-sort-partial"
              >
                Сортировка по столбцу — среди загруженных {rows.length} из {total}
              </span>
            )}
          </td>
        </tr>
      )}
      {q.isError && (
        <tr>
          <td colSpan={colSpan} className="py-2 pr-2" style={indent}>
            <ErrorBox error={q.error} what="следующую страницу" />
          </td>
        </tr>
      )}
    </>
  );
}

function TreeNode({
  row,
  dim,
  depth,
  path,
  base,
  baseCmp,
  cogs,
}: {
  row: TreeRow;
  dim: string;
  depth: number;
  path: PathStep[];
  base: ShareBase | null;
  baseCmp: ShareBase | null;
  cogs: TreeCogs;
}): React.ReactElement {
  const openSet = useContext(OpenCtx);
  const { filters } = useTreeCtx();
  const childPath = useMemo(
    () => [...path, { dim, key: row.key }],
    [path, dim, row.key],
  );
  const key = pathKey(childPath);
  const hideMargin = marginHidden(childPath.map((p) => p.dim));
  const open = row.has_children && openSet.has(key);
  const nextBase = useMemo(
    () => childBase(row.cur, row.label, base),
    [row, base],
  );
  const nextBaseCmp = useMemo(
    () => childBase(row.cmp, row.label, baseCmp),
    [row, baseCmp],
  );
  return (
    <>
      <NodeRow
        row={row}
        dim={dim}
        depth={depth}
        open={open}
        toggleKey={row.has_children ? key : null}
        base={base}
        baseCmp={baseCmp}
        hideMargin={hideMargin}
        cogs={cogs}
        legal={filters.legal}
        alloc={filters.alloc}
      />
      {open && (
        <TreeLevel
          path={childPath}
          depth={depth + 1}
          base={nextBase}
          baseCmp={nextBaseCmp}
        />
      )}
    </>
  );
}

const NodeRow = React.memo(function NodeRow({
  row,
  dim,
  depth,
  open,
  toggleKey,
  base,
  baseCmp,
  hideMargin,
  cogs,
  legal,
  alloc,
}: {
  row: TreeRow;
  dim: string;
  depth: number;
  open: boolean;
  /** ключ узла для раскрытия; null — лист */
  toggleKey: string | null;
  base: ShareBase | null;
  baseCmp: ShareBase | null;
  hideMargin: boolean;
  /** колонки себестоимости (null — у площадки их нет) */
  cogs: TreeCogs;
  legal: string | null;
  alloc: boolean;
}): React.ReactElement {
  const { hasCmp, mpLabel, dimLabel, toggle, openCard } = useTreeCtx();
  const mp = useMp();
  const cols = treeColumns(hasCmp);
  // карточка артикула: у «Артикула» — ключ узла, у «SKU» — его offer_id (нет — значка нет)
  const cardKey = row.product
    ? dim === "article"
      ? row.key || null
      : dim === "sku"
        ? row.product.offer_id
        : null
    : null;
  const onCard =
    cardKey !== null ? () => openCard({ kind: "article", key: cardKey }) : undefined;
  const onToggle = toggleKey !== null ? () => toggle(toggleKey) : undefined;
  const marks = nodeMarks(row.flags, dim);
  // пустой номер («supply:») — разбирать нечего: блок не рендерим; нет карточки поставки у
  // площадки — нет ни значка, ни диагноза
  const supplyNumber =
    (mp.cards.includes("supply") && dim === "obj" && row.key.startsWith("supply:")
      ? row.key.slice(7)
      : "") || null;
  // кампания: номер — только цифры (как ключ карточки в адресе), иначе значка нет
  const campaignNumber =
    mp.cards.includes("campaign") && dim === "obj" && /^campaign:[0-9]+$/.test(row.key)
      ? row.key.slice(9)
      : null;
  // справочная группа (WB `spp`): в P&L не входит — серым курсивом, с пометкой
  const reference = row.flags.reference === true;
  // первая строка узла (тип, имя, пометки) и подпись отдельно: значок карточки ставится в конец
  // первой строки — иначе длинная подпись (название товара) растягивает блок, и значок уезжает
  // к правому краю ячейки
  const labelLine = (
    <>
      <span className="mr-1.5 text-[10.5px] uppercase tracking-wide text-muted-foreground">
        {dimLabel(dim)}
      </span>
      <span
        className={cn("font-medium", reference && "italic text-muted-foreground")}
        data-testid={reference ? "tree-reference" : undefined}
      >
        {row.label}
      </span>
      {reference && (
        <span
          className="ml-1.5 rounded border px-1 text-[10.5px] italic text-muted-foreground"
          title="Справочная группа: в P&L (расходы, маржа) не входит"
        >
          справочно
        </span>
      )}
      {row.product && <ProductLink mpSku={row.product.mp_sku} />}
      {marks.alloc && (
        <MarkChip
          kind="alloc"
          title={`${mpLabel} начислил без артикула — разложено по правилу`}
        />
      )}
      {marks.common && (
        <MarkChip kind="common" title="Не привязано к артикулу" />
      )}
    </>
  );
  const sublabelBlock = (
    <>
      {row.sublabel && (
        <div
          className="truncate text-xs text-muted-foreground"
          // у товара полные названия — в карточке, нативная подсказка ей бы мешала
          title={row.product ? undefined : row.sublabel}
        >
          {row.sublabel}
        </div>
      )}
    </>
  );
  const labelBlock = (
    <>
      {labelLine}
      {sublabelBlock}
    </>
  );
  return (
    <tr
      className={cn(
        "border-t hover:bg-accent/30",
        onToggle && "cursor-pointer",
      )}
      onClick={onToggle}
      aria-expanded={onToggle ? open : undefined}
      data-testid="tree-row"
    >
      <td className="py-1 pr-2" style={{ paddingLeft: `${8 + depth * 18}px` }}>
        <div className="flex items-start gap-1">
          <span className="mt-0.5 w-4 shrink-0 text-muted-foreground">
            {onToggle &&
              (open ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              ))}
          </span>
          <div className="min-w-0">
            {row.product ? (
              // артикул/SKU: подпись — название 1С (иначе площадки), наведение — карточка товара
              // значок карточки — рядом с якорем, не внутри него (кнопка в фокусируемом якоре)
              <>
                <div className="flex items-start gap-1">
                  <ProductHover product={row.product} onOpenCard={onCard}>
                    {labelLine}
                  </ProductHover>
                  {onCard && (
                    <CardButton
                      label={`Карточка артикула ${cardKey}`}
                      title="Карточка артикула"
                      onOpen={onCard}
                    />
                  )}
                </div>
                {sublabelBlock}
              </>
            ) : supplyNumber ? (
              <>
                <div className="flex items-start gap-1">
                  <div className="min-w-0">{labelLine}</div>
                  <CardButton
                    label={`Карточка поставки ${supplyNumber}`}
                    title="Карточка поставки"
                    onOpen={() => openCard({ kind: "supply", key: supplyNumber })}
                  />
                </div>
                {sublabelBlock}
              </>
            ) : campaignNumber ? (
              <>
                <div className="flex items-start gap-1">
                  <div className="min-w-0">{labelLine}</div>
                  <CardButton
                    label={`Карточка кампании ${campaignNumber}`}
                    title="Карточка рекламной кампании"
                    onOpen={() => openCard({ kind: "campaign", key: campaignNumber })}
                  />
                </div>
                {sublabelBlock}
              </>
            ) : (
              labelBlock
            )}
            {supplyNumber && (
              <SupplyIssuesDetails
                orderNumber={supplyNumber}
                legal={legal}
                alloc={alloc}
              />
            )}
          </div>
        </div>
      </td>
      {treeCompactCells(row, base, baseCmp, hasCmp, hideMargin, cogs).map((c, i) => (
        <CompactTd
          key={i}
          cell={c}
          className={cn(hasCmp && cols[i]!.role === "value" && "border-l")}
        />
      ))}
    </tr>
  );
});
